import React, {
  useCallback, useEffect, useRef,
} from 'react';
import { StyledOcticon } from '@primer/react';

import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';

import ModelSingleSelectionItem from './ModelSingleSelectionItem';
import VerticalGradientOverlay from '../../common/overlays/VerticalGradientOverlay';

import ErrorLabel from '../../common/Labels/ErrorLabel';
import ErrorSlate from '../../ErrorSlate/ErrorSlate';

import ModelsIcon from '../../common/icons/ModelsIcon';

const ModelItemWithRef = React.forwardRef((props, ref) => (
  <div ref={ref}>
    <ModelSingleSelectionItem {...props} />
  </div>
))

const EmptyModelListSlate = () => (
  <div className="p-4 flex-grow flex flex-col justify-center items-center">
    <StyledOcticon
      className="mb-2 text-brand"
      icon={ModelsIcon}
      size={16}
    />
    <ErrorLabel>
      There are no models to select from
    </ErrorLabel>
  </div>
)

const ModelRow = ({ index, style, data }) => {
  const {
    models,
    listRef,
    rowHeights,
    onSelect,
    isModelSelected,
    isModelInOriginalSteps
  } = data
  const rowRef = useRef({});
  const selectedModel = models[index];

  const setRowHeight = useCallback((rowIndex, size) => {
    listRef?.current?.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [rowIndex]: size }
  }, [listRef, rowHeights]);

  useEffect(() => {
    if (rowRef.current) {
      setRowHeight(index, rowRef.current.clientHeight);
    }
  }, [index, setRowHeight]);

  return (
    <div style={style}>
      <ModelItemWithRef
        ref={rowRef}
        key={selectedModel?.id}
        index={index}
        model={selectedModel}
        onSelect={onSelect}
        selected={isModelSelected(selectedModel?.modelID)}
        disabled={isModelInOriginalSteps(selectedModel?.modelID)}
      />
    </div>
  )
};

const ModelSingleSelectionList = ({ 
  width,
  models,
  listRef,
  rowHeights,
  onSelect,
  isModelSelected,
  isModelInOriginalSteps,
  height,
  getRowHeight
}) => (
  <VariableSizeList
    ref={listRef}
    height={height}
    itemCount={models?.length}
    estimatedItemSize={80}
    itemSize={getRowHeight}
    width={width}
    itemData={{
      models,
      listRef,
      rowHeights,
      onSelect,
      isModelSelected,
      isModelInOriginalSteps
    }}
  >
    {ModelRow}
  </VariableSizeList>
)

export const ModelSingleSelectionView = ({
  models = [],
  error = null,
  isModelSelected = () => false, 
  isModelInOriginalSteps = () => false, 
  onSelect = () => {}, 
  height = 400,
}) => {
  const listRef = useRef({});
  const rowHeights = useRef({});
  
  const getRowHeight = useCallback((index) => {
    const rowHeight = rowHeights?.current[index]
    return rowHeight || 106;
  }, [rowHeights]);
  
  return (
    <div id="model-selection-view" className="flex flex-grow p-6">
      {
        error ? (
          <ErrorSlate
            subText="Models are not available at the moment"
            errorText={null}
          />
        ) : !models?.length ? (
          <EmptyModelListSlate />
        ): (
          <>
            <VerticalGradientOverlay className="top-0 left-0 right-0" fromDirection="b" />
            <AutoSizer disableHeight>
              {({ width }) => (
                <ModelSingleSelectionList
                  width={width}
                  models={models}
                  listRef={listRef}
                  rowHeights={rowHeights}
                  onSelect={onSelect}
                  isModelSelected={isModelSelected}
                  isModelInOriginalSteps={isModelInOriginalSteps}
                  height={height}
                  getRowHeight={getRowHeight}
                />
              )}
            </AutoSizer>
            <VerticalGradientOverlay className="bottom-0 left-0 right-0" fromDirection="t" />
          </>
        )
      }
    </div>
  )
}

export default ModelSingleSelectionView;
